import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { useToast } from '../hooks/use-toast';
import { Toaster } from '../components/ui/toaster';
import { saveSwipePreferences, getSwipePreferences } from '../services/api';
import { SwipeMatchLogo } from '../components/SwipeMatchLogo';
import { 
  ArrowRight, ArrowLeft, Check, X, Home, MapPin, Euro,
  GraduationCap, Briefcase, Building2, Globe2, Heart, Users,
  Sparkles, MessageSquare, Zap, Loader2,
  Calendar, User, Dog, FileCheck, Search
} from 'lucide-react';

const SwipeMatchPage = () => {
  const navigate = useNavigate();
  const { isAuthenticated, user, loading: authLoading } = useAuth();
  const { language } = useLanguage();
  const { toast } = useToast();
  const [currentStep, setCurrentStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [citySearch, setCitySearch] = useState('');
  const [checkingPreferences, setCheckingPreferences] = useState(true);
  
  const [preferences, setPreferences] = useState({
    profileType: '',
    nationality: '',
    cities: [],
    types: [],
    minPrice: '',
    maxPrice: '',
    rentalDuration: '',
    numberOfPersons: 1,
    hasPets: null,
    needsRegistration: null,
    searchRadius: 0,
    autoMessage: ''
  });

  const TOTAL_STEPS = 5;
  const isVerhuurder = user?.userType === 'verhuurder';

  // Redirect verhuurders to their own flow
  useEffect(() => {
    if (authLoading) return;
    if (!isAuthenticated) {
      toast({ 
        title: language === 'nl' ? 'Account vereist' : 'Account required',
        description: language === 'nl' ? 'Maak eerst een account aan om te starten' : 'Create an account first to get started'
      });
      navigate('/registreren');
      return;
    }
    
    // If user is a verhuurder, redirect to landlord onboarding
    if (isVerhuurder) {
      navigate('/verhuurder/onboarding');
      return;
    }
  }, [isAuthenticated, authLoading, navigate, toast, isVerhuurder, language]);

  useEffect(() => {
    if (authLoading) return;
    
    const loadPreferences = async () => {
      if (!isAuthenticated) {
        setCheckingPreferences(false);
        return;
      }
      try {
        const res = await getSwipePreferences();
        if (res.preferences) {
          const prefs = res.preferences;
          setPreferences(prev => ({
            ...prev,
            profileType: prefs.profileType || '',
            nationality: prefs.nationality || '',
            cities: prefs.cities || [],
            types: prefs.types || [],
            minPrice: prefs.minPrice || '',
            maxPrice: prefs.maxPrice || '',
            rentalDuration: prefs.rentalDuration || '',
            numberOfPersons: prefs.numberOfPersons || 1,
            hasPets: prefs.hasPets ?? null,
            needsRegistration: prefs.needsRegistration ?? null
          }));
          
          // If user already has basic preferences set, skip to swipe
          // Only require cities and types - profileType is checked separately
          if (prefs.cities?.length > 0 && prefs.types?.length > 0) {
            // User has already completed onboarding, go to swipe
            navigate('/swipematch/swipe');
            return; // Don't set checkingPreferences to false, we're navigating away
          }
        }
        setCheckingPreferences(false);
      } catch (error) {
        console.error('Error loading preferences:', error);
        setCheckingPreferences(false);
      }
    };
    
    loadPreferences();
  }, [isAuthenticated, authLoading, navigate]);

  // Profile types with icons
  const profileTypes = language === 'nl' ? [
    { id: 'student', label: 'Student', icon: GraduationCap, desc: 'HBO/WO student', tagline: 'Campusleven & studiefocus' },
    { id: 'starter', label: 'Starter', icon: Briefcase, desc: 'Net afgestudeerd', tagline: 'Eerste eigen plek' },
    { id: 'professional', label: 'Professional', icon: Building2, desc: 'Vast inkomen', tagline: 'Carrière & stabiliteit' },
    { id: 'expat', label: 'Expat', icon: Globe2, desc: 'Internationaal', tagline: 'Nieuw in Nederland' },
    { id: 'koppel', label: 'Koppel', icon: Heart, desc: 'Samen zoeken', tagline: 'Jullie eerste thuis' },
    { id: 'gezin', label: 'Gezin', icon: Users, desc: 'Met kinderen', tagline: 'Ruimte voor groei' }
  ] : [
    { id: 'student', label: 'Student', icon: GraduationCap, desc: 'University student', tagline: 'Campus life & study focus' },
    { id: 'starter', label: 'Starter', icon: Briefcase, desc: 'Just graduated', tagline: 'First own place' },
    { id: 'professional', label: 'Professional', icon: Building2, desc: 'Stable income', tagline: 'Career & stability' },
    { id: 'expat', label: 'Expat', icon: Globe2, desc: 'International', tagline: 'New to Netherlands' },
    { id: 'koppel', label: 'Couple', icon: Heart, desc: 'Searching together', tagline: 'Your first home together' },
    { id: 'gezin', label: 'Family', icon: Users, desc: 'With children', tagline: 'Room to grow' }
  ];

  const cities = [
    { id: 'Amsterdam', label: 'Amsterdam', popular: true },
    { id: 'Rotterdam', label: 'Rotterdam', popular: true },
    { id: 'Utrecht', label: 'Utrecht', popular: true },
    { id: 'Den Haag', label: 'Den Haag', popular: true },
    { id: 'Eindhoven', label: 'Eindhoven', popular: true },
    { id: 'Groningen', label: 'Groningen', popular: true },
    { id: 'Tilburg', label: 'Tilburg', popular: true },
    { id: 'Almere', label: 'Almere' },
    { id: 'Breda', label: 'Breda', popular: true },
    { id: 'Nijmegen', label: 'Nijmegen', popular: true },
    { id: 'Arnhem', label: 'Arnhem', popular: true },
    { id: 'Haarlem', label: 'Haarlem' },
    { id: 'Enschede', label: 'Enschede' },
    { id: 'Maastricht', label: 'Maastricht' },
    { id: 'Leiden', label: 'Leiden' },
    { id: 'Delft', label: 'Delft' },
    { id: 'Amersfoort', label: 'Amersfoort' },
    { id: 'Zwolle', label: 'Zwolle' },
    { id: 'Apeldoorn', label: 'Apeldoorn' },
    { id: 'Dordrecht', label: 'Dordrecht' },
    { id: "'s-Hertogenbosch", label: "'s-Hertogenbosch" },
    { id: 'Hoofddorp', label: 'Hoofddorp' },
    { id: 'Zoetermeer', label: 'Zoetermeer' },
    { id: 'Deventer', label: 'Deventer' },
    { id: 'Alkmaar', label: 'Alkmaar' },
    { id: 'Heerlen', label: 'Heerlen' },
    { id: 'Venlo', label: 'Venlo' },
    { id: 'Leeuwarden', label: 'Leeuwarden' },
    { id: 'Hilversum', label: 'Hilversum' },
    { id: 'Amstelveen', label: 'Amstelveen' },
    { id: 'Oss', label: 'Oss' },
    { id: 'Schiedam', label: 'Schiedam' },
    { id: 'Helmond', label: 'Helmond' },
    { id: 'Vlaardingen', label: 'Vlaardingen' },
    { id: 'Almelo', label: 'Almelo' },
    { id: 'Gouda', label: 'Gouda' },
    { id: 'Zaandam', label: 'Zaandam' },
    { id: 'Lelystad', label: 'Lelystad' },
    { id: 'Purmerend', label: 'Purmerend' },
    { id: 'Roosendaal', label: 'Roosendaal' },
    { id: 'Hoorn', label: 'Hoorn' },
    { id: 'Ede', label: 'Ede' },
    { id: 'Alphen aan den Rijn', label: 'Alphen aan den Rijn' },
    { id: 'Bergen op Zoom', label: 'Bergen op Zoom' },
    { id: 'Nieuwegein', label: 'Nieuwegein' },
    { id: 'Veenendaal', label: 'Veenendaal' },
    { id: 'Zeist', label: 'Zeist' },
    { id: 'Middelburg', label: 'Middelburg' },
    { id: 'Doetinchem', label: 'Doetinchem' },
    { id: 'Roermond', label: 'Roermond' },
    { id: 'Kampen', label: 'Kampen' },
    { id: 'Woerden', label: 'Woerden' },
    { id: 'Assen', label: 'Assen' },
    { id: 'Sittard', label: 'Sittard' },
    { id: 'Weert', label: 'Weert' },
    { id: 'Emmen', label: 'Emmen' },
    { id: 'Rijswijk', label: 'Rijswijk' },
    { id: 'Den Helder', label: 'Den Helder' },
    { id: 'Hoogeveen', label: 'Hoogeveen' },
    { id: 'Meppel', label: 'Meppel' },
    { id: 'Drachten', label: 'Drachten' },
    { id: 'Sneek', label: 'Sneek' },
    { id: 'Heerenveen', label: 'Heerenveen' },
    { id: 'Tiel', label: 'Tiel' },
    { id: 'Gorinchem', label: 'Gorinchem' },
    { id: 'Harderwijk', label: 'Harderwijk' },
    { id: 'Wageningen', label: 'Wageningen' },
    { id: 'Barneveld', label: 'Barneveld' },
    { id: 'Terneuzen', label: 'Terneuzen' },
    { id: 'Goes', label: 'Goes' },
    { id: 'Vlissingen', label: 'Vlissingen' },
    { id: 'Barendrecht', label: 'Barendrecht' },
    { id: 'Ridderkerk', label: 'Ridderkerk' },
    { id: 'Waalwijk', label: 'Waalwijk' },
    { id: 'Boxtel', label: 'Boxtel' },
    { id: 'Oosterhout', label: 'Oosterhout' },
    { id: 'Oldenzaal', label: 'Oldenzaal' },
    { id: 'Hengelo', label: 'Hengelo' },
    { id: 'Zutphen', label: 'Zutphen' },
    { id: 'Winterswijk', label: 'Winterswijk' },
    { id: 'Zevenaar', label: 'Zevenaar' },
    { id: 'Wijchen', label: 'Wijchen' },
    { id: 'Cuijk', label: 'Cuijk' },
    { id: 'Uden', label: 'Uden' },
    { id: 'Veghel', label: 'Veghel' },
    { id: 'Best', label: 'Best' },
    { id: 'Veldhoven', label: 'Veldhoven' },
    { id: 'Valkenswaard', label: 'Valkenswaard' },
    { id: 'Venray', label: 'Venray' },
    { id: 'Horst', label: 'Horst' }
  ];

  // Filter cities based on search
  const filteredCities = cities.filter(city => 
    city.label.toLowerCase().includes(citySearch.toLowerCase())
  );

  const housingTypes = [
    { id: 'kamer', label: language === 'nl' ? 'Kamer' : 'Room', price: language === 'nl' ? 'Vanaf €400' : 'From €400', desc: language === 'nl' ? 'Gedeelde voorzieningen' : 'Shared facilities' },
    { id: 'studio', label: 'Studio', price: language === 'nl' ? 'Vanaf €700' : 'From €700', desc: 'Alles-in-één' },
    { id: 'appartement', label: language === 'nl' ? 'Appartement' : 'Apartment', price: language === 'nl' ? 'Vanaf €1000' : 'From €1000', desc: language === 'nl' ? 'Volledige woning' : 'Full apartment' },
    { id: 'eengezinswoning', label: language === 'nl' ? 'Eengezinswoning' : 'Family home', price: language === 'nl' ? 'Vanaf €1500' : 'From €1500', desc: language === 'nl' ? 'Voor gezinnen' : 'For families' }
  ];

  // Rental duration options
  const rentalDurations = [
    { id: 'flexible', label: language === 'nl' ? 'Flexibel' : 'Flexible', desc: language === 'nl' ? 'Alle opties' : 'All options' },
    { id: 'short_1_3', label: language === 'nl' ? '1-3 maanden' : '1-3 months', desc: 'Short stay' },
    { id: 'medium_3_6', label: language === 'nl' ? '3-6 maanden' : '3-6 months', desc: language === 'nl' ? 'Tijdelijk' : 'Temporary' },
    { id: 'medium_6_12', label: language === 'nl' ? '6-12 maanden' : '6-12 months', desc: language === 'nl' ? 'Middellang' : 'Medium term' },
    { id: 'long_1_year', label: language === 'nl' ? '1 jaar+' : '1 year+', desc: language === 'nl' ? 'Langdurig' : 'Long term' },
    { id: 'indefinite', label: language === 'nl' ? 'Onbepaalde tijd' : 'Indefinite', desc: language === 'nl' ? 'Geen einddatum' : 'No end date' }
  ];

  const toggleCity = (cityId) => {
    setPreferences(prev => ({
      ...prev,
      cities: prev.cities.includes(cityId)
        ? prev.cities.filter(c => c !== cityId)
        : [...prev.cities, cityId]
    }));
  };

  const toggleType = (typeId) => {
    setPreferences(prev => ({
      ...prev,
      types: prev.types.includes(typeId)
        ? prev.types.filter(t => t !== typeId)
        : [...prev.types, typeId]
    }));
  };

  const handleSubmit = async () => {
    if (!isAuthenticated) {
      navigate('/registreren');
      return;
    }

    setLoading(true);
    try {
      await saveSwipePreferences({
        ...preferences,
        minPrice: preferences.minPrice ? parseFloat(preferences.minPrice) : null,
        maxPrice: preferences.maxPrice ? parseFloat(preferences.maxPrice) : null
      });
      
      toast({ title: language === 'nl' ? '🎉 Je profiel is klaar! Start met swipen' : '🎉 Your profile is ready! Start swiping' });
      navigate('/swipematch/swipe');
    } catch (error) {
      console.error('Error saving preferences:', error);
      toast({ title: language === 'nl' ? 'Er ging iets mis' : 'Something went wrong', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  const canProceed = () => {
    switch (currentStep) {
      case 1: return preferences.profileType !== '';
      case 2: return preferences.cities.length > 0;
      case 3: return preferences.types.length > 0 && preferences.rentalDuration !== '';
      case 4: return preferences.hasPets !== null && preferences.needsRegistration !== null;
      case 5: return true; // autoMessage is optional
      default: return false;
    }
  };

  // Loading state - show while auth is loading OR while checking preferences
  if (authLoading || checkingPreferences) {
    return (
      <div className="fixed inset-0 bg-[#F7F7F7] flex items-center justify-center">
        <div className="text-center">
          <div className="w-20 h-20 bg-[#2E7D32] rounded-2xl flex items-center justify-center mx-auto mb-6 animate-pulse">
            <Home className="w-10 h-10 text-white" />
          </div>
          <p className="text-[#9E9E9E]">{language === 'nl' ? 'Laden...' : 'Loading...'}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-[#F7F7F7] overflow-hidden" data-testid="swipematch-onboarding">
      <Toaster />

      {/* Header */}
      <div className="absolute top-0 left-0 right-0 z-30 bg-[#F7F7F7]/95 backdrop-blur-sm border-b border-[#E5E5E5]">
        <div className="max-w-2xl mx-auto px-4 py-3 flex items-center justify-between">
          <button onClick={() => currentStep > 1 ? setCurrentStep(currentStep - 1) : navigate('/')}>
            {currentStep > 1 ? (
              <ArrowLeft className="w-6 h-6 text-[#2A2A2A]" />
            ) : (
              <X className="w-6 h-6 text-[#2A2A2A]" />
            )}
          </button>
          
          <SwipeMatchLogo size="small" variant="color" />
          
          <span className="text-sm font-bold text-[#2E7D32]">{currentStep}/{TOTAL_STEPS}</span>
        </div>
        
        {/* Progress bar */}
        <div className="h-1 bg-[#E5E5E5]">
          <div 
            className="h-full bg-[#2E7D32] transition-all duration-500"
            style={{ width: `${(currentStep / TOTAL_STEPS) * 100}%` }}
          />
        </div>
      </div>

      {/* Main Content */}
      <div className="h-full pt-16 pb-24 overflow-y-auto">
        <div className="max-w-xl mx-auto px-4 py-6">
          
          {/* Step 1: Profile Type - FULL SCREEN CLICKBAIT */}
          {currentStep === 1 && (
            <div className="space-y-6 animate-fadeIn">
              <div className="text-center mb-8">
                <div className="inline-flex items-center gap-2 bg-[#2E7D32]/10 text-[#2E7D32] px-4 py-2 rounded-full text-sm font-bold mb-4">
                  <Zap className="w-4 h-4" />
                  {language === 'nl' ? 'SWIPE GRATIS!' : 'SWIPE FOR FREE!'}
                </div>
                <h1 className="text-4xl font-black text-[#2A2A2A] mb-3 leading-tight">
                  {language === 'nl' ? 'Wie ben jij?' : 'Who are you?'}
                </h1>
                <p className="text-lg text-[#9E9E9E]">
                  {language === 'nl' ? 'Verhuurders matchen met het juiste profiel' : 'Landlords match with the right profile'}
                </p>
              </div>

              <div className="grid grid-cols-2 gap-3">
                {profileTypes.map((type) => {
                  const Icon = type.icon;
                  const isSelected = preferences.profileType === type.id;
                  return (
                    <button
                      key={type.id}
                      onClick={() => setPreferences(prev => ({ ...prev, profileType: type.id }))}
                      className={`p-5 rounded-2xl border-2 text-left transition-all relative ${
                        isSelected
                          ? 'border-[#2E7D32] bg-[#2E7D32]/5 shadow-lg scale-[1.02]'
                          : 'border-[#E5E5E5] bg-white hover:border-[#2E7D32]/50 hover:shadow-md'
                      }`}
                      data-testid={`profile-${type.id}`}
                    >
                      <div className={`w-12 h-12 rounded-xl flex items-center justify-center mb-3 ${
                        isSelected ? 'bg-[#2E7D32] text-white' : 'bg-[#F7F7F7] text-[#9E9E9E]'
                      }`}>
                        <Icon className="w-6 h-6" />
                      </div>
                      <h3 className="font-black text-lg text-[#2A2A2A]">{type.label}</h3>
                      <p className="text-sm text-[#2E7D32] font-medium">{type.tagline}</p>
                      {isSelected && (
                        <div className="absolute top-3 right-3 w-6 h-6 bg-[#2E7D32] rounded-full flex items-center justify-center">
                          <Check className="w-4 h-4 text-white" />
                        </div>
                      )}
                    </button>
                  );
                })}
              </div>

              {/* Stats banner */}
              <div className="bg-gradient-to-r from-[#1F3A5F] to-[#2E7D32] rounded-2xl p-5 text-white">
                <div className="grid grid-cols-3 gap-4 text-center">
                  <div>
                    <div className="text-2xl font-black">82%</div>
                    <div className="text-xs opacity-80">{language === 'nl' ? 'Vindt match binnen 2 weken' : 'Finds match within 2 weeks'}</div>
                  </div>
                  <div>
                    <div className="text-2xl font-black">320+</div>
                    <div className="text-xs opacity-80">{language === 'nl' ? 'Actieve woningen' : 'Active properties'}</div>
                  </div>
                  <div>
                    <div className="text-2xl font-black">€0</div>
                    <div className="text-xs opacity-80">{language === 'nl' ? 'Om te starten' : 'To get started'}</div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Step 2: Cities */}
          {currentStep === 2 && (
            <div className="space-y-6 animate-fadeIn">
              <div className="text-center mb-6">
                <h1 className="text-4xl font-black text-[#2A2A2A] mb-3">
                  {language === 'nl' ? 'Waar zoek je?' : 'Where are you looking?'}
                </h1>
                <p className="text-lg text-[#9E9E9E]">
                  {language === 'nl' ? 'Selecteer één of meerdere steden' : 'Select one or more cities'}
                </p>
              </div>

              {/* Search bar */}
              <div className="relative">
                <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-[#9E9E9E]" />
                <Input
                  type="text"
                  placeholder={language === 'nl' ? 'Zoek een stad...' : 'Search a city...'}
                  value={citySearch}
                  onChange={(e) => setCitySearch(e.target.value)}
                  className="pl-12 h-12 rounded-xl border-[#E5E5E5] bg-white"
                />
              </div>

              {/* Selected cities count */}
              {preferences.cities.length > 0 && (
                <div className="flex flex-wrap gap-2">
                  {preferences.cities.map(cityId => (
                    <span key={cityId} className="inline-flex items-center gap-1 bg-[#2E7D32] text-white text-sm px-3 py-1 rounded-full">
                      {cityId}
                      <button onClick={() => toggleCity(cityId)} className="hover:bg-white/20 rounded-full p-0.5">
                        <X className="w-3 h-3" />
                      </button>
                    </span>
                  ))}
                </div>
              )}

              {/* Search Radius - Now positioned prominently */}
              {preferences.cities.length > 0 && (
                <div className="bg-[#2E7D32]/5 rounded-xl p-4 border-2 border-[#2E7D32]/20">
                  <p className="text-sm font-bold text-[#2A2A2A] mb-3 flex items-center gap-2">
                    <span>📍</span>
                    {language === 'nl' ? 'Zoek ook in de omgeving?' : 'Also search nearby?'}
                  </p>
                  <div className="flex gap-2">
                    {[
                      { value: 0, label: language === 'nl' ? 'Alleen geselecteerde steden' : 'Selected cities only', short: language === 'nl' ? 'Exact' : 'Exact' },
                      { value: 10, label: '+ 10 km', short: '+10km' },
                      { value: 20, label: '+ 20 km', short: '+20km' }
                    ].map((option) => (
                      <button
                        key={option.value}
                        onClick={() => setPreferences(prev => ({ ...prev, searchRadius: option.value }))}
                        className={`flex-1 px-3 py-2.5 rounded-xl text-sm font-medium transition-all border-2 ${
                          preferences.searchRadius === option.value
                            ? 'border-[#2E7D32] bg-[#2E7D32] text-white'
                            : 'border-[#E5E5E5] bg-white text-[#666] hover:border-[#2E7D32]/50'
                        }`}
                      >
                        {option.value === 0 ? option.short : option.label}
                      </button>
                    ))}
                  </div>
                  {preferences.searchRadius > 0 && (
                    <p className="text-xs text-[#2E7D32] mt-2 text-center font-medium">
                      ✓ {language === 'nl' 
                        ? `Je ziet ook woningen binnen ${preferences.searchRadius}km van je geselecteerde steden` 
                        : `You'll also see properties within ${preferences.searchRadius}km of your selected cities`}
                    </p>
                  )}
                </div>
              )}

              <div className="grid grid-cols-2 gap-3 max-h-[45vh] overflow-y-auto">
                {filteredCities.map((city) => {
                  const isSelected = preferences.cities.includes(city.id);
                  return (
                    <button
                      key={city.id}
                      onClick={() => toggleCity(city.id)}
                      className={`p-4 rounded-xl border-2 text-left transition-all relative ${
                        isSelected
                          ? 'border-[#2E7D32] bg-[#2E7D32]/5'
                          : 'border-[#E5E5E5] bg-white hover:border-[#2E7D32]/50'
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        <div>
                          <span className="font-bold text-[#2A2A2A]">{city.label}</span>
                          {city.popular && (
                            <span className="ml-2 text-[10px] bg-[#2E7D32] text-white px-1.5 py-0.5 rounded-full font-bold">
                              HOT
                            </span>
                          )}
                        </div>
                        {isSelected && (
                          <div className="w-5 h-5 bg-[#2E7D32] rounded-full flex items-center justify-center">
                            <Check className="w-3 h-3 text-white" />
                          </div>
                        )}
                      </div>
                    </button>
                  );
                })}
              </div>

              <p className="text-center text-sm text-[#9E9E9E]">
                {preferences.cities.length === 0 
                  ? (language === 'nl' ? 'Selecteer minstens één stad' : 'Select at least one city')
                  : `${preferences.cities.length} ${preferences.cities.length === 1 ? (language === 'nl' ? 'stad' : 'city') : (language === 'nl' ? 'steden' : 'cities')} ${language === 'nl' ? 'geselecteerd' : 'selected'}`
                }
              </p>
            </div>
          )}

          {/* Step 3: Housing Type, Duration & Budget */}
          {currentStep === 3 && (
            <div className="space-y-6 animate-fadeIn">
              <div className="text-center mb-6">
                <h1 className="text-4xl font-black text-[#2A2A2A] mb-3">
                  {language === 'nl' ? 'Wat zoek je?' : 'What are you looking for?'}
                </h1>
                <p className="text-lg text-[#9E9E9E]">
                  {language === 'nl' ? 'Type woning, duur en budget' : 'Property type, duration & budget'}
                </p>
              </div>

              {/* Housing types */}
              <div>
                <label className="text-sm font-bold text-[#2A2A2A] mb-3 block">
                  {language === 'nl' ? 'Type woning' : 'Property type'}
                </label>
                <div className="grid grid-cols-2 gap-3">
                  {housingTypes.map((type) => {
                    const isSelected = preferences.types.includes(type.id);
                    return (
                      <button
                        key={type.id}
                        onClick={() => toggleType(type.id)}
                        className={`p-4 rounded-xl border-2 text-left transition-all ${
                          isSelected
                            ? 'border-[#2E7D32] bg-[#2E7D32]/5'
                            : 'border-[#E5E5E5] bg-white hover:border-[#2E7D32]/50'
                        }`}
                      >
                        <div className="flex items-center justify-between">
                          <div>
                            <h3 className="font-bold text-[#2A2A2A]">{type.label}</h3>
                            <p className="text-xs text-[#9E9E9E]">{type.desc}</p>
                          </div>
                          {isSelected && (
                            <div className="w-5 h-5 bg-[#2E7D32] rounded-full flex items-center justify-center">
                              <Check className="w-3 h-3 text-white" />
                            </div>
                          )}
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Rental Duration */}
              <div>
                <label className="text-sm font-bold text-[#2A2A2A] mb-3 block flex items-center gap-2">
                  <Calendar className="w-4 h-4 text-[#2E7D32]" />
                  {language === 'nl' ? 'Hoe lang wil je huren?' : 'How long do you want to rent?'}
                </label>
                <div className="grid grid-cols-2 gap-2">
                  {rentalDurations.map((duration) => {
                    const isSelected = preferences.rentalDuration === duration.id;
                    return (
                      <button
                        key={duration.id}
                        onClick={() => setPreferences(prev => ({ ...prev, rentalDuration: duration.id }))}
                        className={`p-3 rounded-xl border-2 text-left transition-all ${
                          isSelected
                            ? 'border-[#2E7D32] bg-[#2E7D32]/5'
                            : 'border-[#E5E5E5] bg-white hover:border-[#2E7D32]/50'
                        }`}
                      >
                        <span className="font-bold text-sm text-[#2A2A2A]">{duration.label}</span>
                        <p className="text-xs text-[#9E9E9E]">{duration.desc}</p>
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Number of Persons */}
              <div className="bg-white rounded-xl p-4 border border-[#E5E5E5]">
                <label className="text-sm font-bold text-[#2A2A2A] mb-3 block flex items-center gap-2">
                  <User className="w-4 h-4 text-[#2E7D32]" />
                  {language === 'nl' ? 'Aantal personen' : 'Number of persons'}
                </label>
                <div className="flex items-center gap-4">
                  <button
                    onClick={() => setPreferences(prev => ({ ...prev, numberOfPersons: Math.max(1, prev.numberOfPersons - 1) }))}
                    className="w-10 h-10 rounded-full border-2 border-[#E5E5E5] flex items-center justify-center hover:border-[#2E7D32] transition-colors"
                  >
                    <span className="text-xl font-bold text-[#2A2A2A]">-</span>
                  </button>
                  <div className="flex-1 text-center">
                    <span className="text-3xl font-black text-[#2A2A2A]">{preferences.numberOfPersons}</span>
                    <p className="text-xs text-[#9E9E9E]">
                      {preferences.numberOfPersons === 1 
                        ? (language === 'nl' ? 'persoon' : 'person') 
                        : (language === 'nl' ? 'personen' : 'persons')}
                    </p>
                  </div>
                  <button
                    onClick={() => setPreferences(prev => ({ ...prev, numberOfPersons: Math.min(10, prev.numberOfPersons + 1) }))}
                    className="w-10 h-10 rounded-full border-2 border-[#E5E5E5] flex items-center justify-center hover:border-[#2E7D32] transition-colors"
                  >
                    <span className="text-xl font-bold text-[#2A2A2A]">+</span>
                  </button>
                </div>
                {preferences.profileType === 'koppel' && preferences.numberOfPersons < 2 && (
                  <p className="text-xs text-[#C04A4A] mt-2">{language === 'nl' ? 'Koppel = minimaal 2 personen' : 'Couple = minimum 2 persons'}</p>
                )}
                {preferences.profileType === 'gezin' && preferences.numberOfPersons < 3 && (
                  <p className="text-xs text-[#C04A4A] mt-2">{language === 'nl' ? 'Gezin = meestal 3+ personen' : 'Family = usually 3+ persons'}</p>
                )}
              </div>

              {/* Budget range */}
              <div className="bg-white rounded-xl p-4 border border-[#E5E5E5]">
                <label className="text-sm font-bold text-[#2A2A2A] mb-3 block flex items-center gap-2">
                  <Euro className="w-4 h-4 text-[#2E7D32]" />
                  {language === 'nl' ? 'Jouw budget per maand' : 'Your monthly budget'}
                </label>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <span className="text-xs text-[#9E9E9E]">Min</span>
                    <Input
                      type="number"
                      placeholder="€400"
                      className="h-12 rounded-xl border-[#E5E5E5]"
                      value={preferences.minPrice}
                      onChange={(e) => setPreferences(prev => ({ ...prev, minPrice: e.target.value }))}
                    />
                  </div>
                  <div>
                    <span className="text-xs text-[#9E9E9E]">Max</span>
                    <Input
                      type="number"
                      placeholder="€1500"
                      className="h-12 rounded-xl border-[#E5E5E5]"
                      value={preferences.maxPrice}
                      onChange={(e) => setPreferences(prev => ({ ...prev, maxPrice: e.target.value }))}
                    />
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Step 4: Pets & Registration */}
          {currentStep === 4 && (
            <div className="space-y-6 animate-fadeIn">
              <div className="text-center mb-6">
                <h1 className="text-4xl font-black text-[#2A2A2A] mb-3">
                  {language === 'nl' ? 'Belangrijk voor verhuurders' : 'Important for landlords'}
                </h1>
                <p className="text-lg text-[#9E9E9E]">
                  {language === 'nl' ? 'Deze info helpt bij betere matches' : 'This info helps with better matches'}
                </p>
              </div>

              {/* Pets */}
              <div className="bg-white rounded-2xl p-5 border border-[#E5E5E5]">
                <label className="text-sm font-bold text-[#2A2A2A] mb-4 block flex items-center gap-2">
                  <Dog className="w-5 h-5 text-[#2E7D32]" />
                  {language === 'nl' ? 'Heb je huisdieren?' : 'Do you have pets?'}
                </label>
                <div className="grid grid-cols-2 gap-3">
                  <button
                    onClick={() => setPreferences(prev => ({ ...prev, hasPets: true }))}
                    className={`p-4 rounded-xl border-2 text-center transition-all ${
                      preferences.hasPets === true
                        ? 'border-[#2E7D32] bg-[#2E7D32]/5'
                        : 'border-[#E5E5E5] bg-white hover:border-[#2E7D32]/50'
                    }`}
                  >
                    <span className="text-2xl mb-1 block">🐕</span>
                    <span className="font-bold text-[#2A2A2A]">{language === 'nl' ? 'Ja' : 'Yes'}</span>
                    <p className="text-xs text-[#9E9E9E]">{language === 'nl' ? 'Ik heb huisdier(en)' : 'I have pet(s)'}</p>
                  </button>
                  <button
                    onClick={() => setPreferences(prev => ({ ...prev, hasPets: false }))}
                    className={`p-4 rounded-xl border-2 text-center transition-all ${
                      preferences.hasPets === false
                        ? 'border-[#2E7D32] bg-[#2E7D32]/5'
                        : 'border-[#E5E5E5] bg-white hover:border-[#2E7D32]/50'
                    }`}
                  >
                    <span className="text-2xl mb-1 block">🚫</span>
                    <span className="font-bold text-[#2A2A2A]">{language === 'nl' ? 'Nee' : 'No'}</span>
                    <p className="text-xs text-[#9E9E9E]">{language === 'nl' ? 'Geen huisdieren' : 'No pets'}</p>
                  </button>
                </div>
              </div>

              {/* Registration */}
              <div className="bg-white rounded-2xl p-5 border border-[#E5E5E5]">
                <label className="text-sm font-bold text-[#2A2A2A] mb-4 block flex items-center gap-2">
                  <FileCheck className="w-5 h-5 text-[#2E7D32]" />
                  {language === 'nl' ? 'Gemeentelijke inschrijving nodig?' : 'Need municipal registration?'}
                </label>
                <p className="text-xs text-[#9E9E9E] mb-4">
                  {language === 'nl' 
                    ? 'Sommige verhuurders staan inschrijving op het adres niet toe' 
                    : 'Some landlords do not allow registration at the address'}
                </p>
                <div className="grid grid-cols-2 gap-3">
                  <button
                    onClick={() => setPreferences(prev => ({ ...prev, needsRegistration: true }))}
                    className={`p-4 rounded-xl border-2 text-center transition-all ${
                      preferences.needsRegistration === true
                        ? 'border-[#2E7D32] bg-[#2E7D32]/5'
                        : 'border-[#E5E5E5] bg-white hover:border-[#2E7D32]/50'
                    }`}
                  >
                    <span className="text-2xl mb-1 block">📋</span>
                    <span className="font-bold text-[#2A2A2A]">{language === 'nl' ? 'Ja, nodig' : 'Yes, needed'}</span>
                    <p className="text-xs text-[#9E9E9E]">{language === 'nl' ? 'Ik wil me inschrijven' : 'I need to register'}</p>
                  </button>
                  <button
                    onClick={() => setPreferences(prev => ({ ...prev, needsRegistration: false }))}
                    className={`p-4 rounded-xl border-2 text-center transition-all ${
                      preferences.needsRegistration === false
                        ? 'border-[#2E7D32] bg-[#2E7D32]/5'
                        : 'border-[#E5E5E5] bg-white hover:border-[#2E7D32]/50'
                    }`}
                  >
                    <span className="text-2xl mb-1 block">✓</span>
                    <span className="font-bold text-[#2A2A2A]">{language === 'nl' ? 'Niet nodig' : 'Not needed'}</span>
                    <p className="text-xs text-[#9E9E9E]">{language === 'nl' ? 'Al ingeschreven elders' : 'Already registered elsewhere'}</p>
                  </button>
                </div>
              </div>

              {/* Summary card */}
              <div className="bg-gradient-to-r from-[#1F3A5F] to-[#2E7D32] rounded-2xl p-5 text-white">
                <h3 className="font-bold mb-3">{language === 'nl' ? 'Jouw zoekprofiel' : 'Your search profile'}</h3>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="opacity-70">{language === 'nl' ? 'Steden' : 'Cities'}</span>
                    <span className="font-bold">{preferences.cities.length} {language === 'nl' ? 'geselecteerd' : 'selected'}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="opacity-70">{language === 'nl' ? 'Personen' : 'Persons'}</span>
                    <span className="font-bold">{preferences.numberOfPersons}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="opacity-70">{language === 'nl' ? 'Huisdieren' : 'Pets'}</span>
                    <span className="font-bold">{preferences.hasPets === true ? (language === 'nl' ? 'Ja' : 'Yes') : preferences.hasPets === false ? (language === 'nl' ? 'Nee' : 'No') : '-'}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="opacity-70">{language === 'nl' ? 'Inschrijving' : 'Registration'}</span>
                    <span className="font-bold">{preferences.needsRegistration === true ? (language === 'nl' ? 'Nodig' : 'Needed') : preferences.needsRegistration === false ? (language === 'nl' ? 'Niet nodig' : 'Not needed') : '-'}</span>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Step 5: Auto Message with AI Generation */}
          {currentStep === 5 && (
            <div className="space-y-6 animate-fadeIn">
              <div className="text-center mb-6">
                <h1 className="text-4xl font-black text-[#2A2A2A] mb-3">
                  {language === 'nl' ? 'Jouw automatisch bericht' : 'Your auto message'}
                </h1>
                <p className="text-lg text-[#9E9E9E]">
                  {language === 'nl' 
                    ? 'Dit bericht wordt automatisch verstuurd als je een woning liked' 
                    : 'This message is sent automatically when you like a property'}
                </p>
              </div>

              <div className="bg-white rounded-2xl p-5 border border-[#E5E5E5]">
                <label className="text-sm font-bold text-[#2A2A2A] mb-2 block flex items-center gap-2">
                  <MessageSquare className="w-5 h-5 text-[#2E7D32]" />
                  {language === 'nl' ? 'Bericht aan verhuurder' : 'Message to landlord'}
                </label>
                <p className="text-xs text-[#9E9E9E] mb-4">
                  {language === 'nl' 
                    ? 'Stel jezelf kort voor. Verhuurders reageren sneller op een persoonlijk bericht.' 
                    : 'Briefly introduce yourself. Landlords respond faster to a personal message.'}
                </p>
                <Textarea
                  data-testid="onboarding-auto-message"
                  placeholder={language === 'nl' 
                    ? 'Bijv: Hallo! Ik ben een student op zoek naar een kamer. Ik studeer aan de UvA en heb een vast inkomen uit mijn bijbaan. Graag hoor ik meer over deze woning!' 
                    : 'E.g.: Hi! I am a student looking for a room. I study at UvA and have a steady income from my part-time job. I would love to hear more about this property!'}
                  value={preferences.autoMessage}
                  onChange={(e) => setPreferences(prev => ({ ...prev, autoMessage: e.target.value }))}
                  className="min-h-[120px] rounded-xl border-2 border-[#E5E5E5] focus:border-[#2E7D32] resize-none text-base"
                  maxLength={500}
                />
                <div className="flex justify-between items-center mt-2">
                  <span className="text-xs text-[#9E9E9E]">
                    {language === 'nl' ? 'Laat leeg voor een standaard bericht' : 'Leave empty for a default message'}
                  </span>
                  <span className={`text-xs font-bold ${(preferences.autoMessage?.length || 0) > 450 ? 'text-[#C04A4A]' : 'text-[#9E9E9E]'}`}>
                    {preferences.autoMessage?.length || 0}/500
                  </span>
                </div>
              </div>

              {/* Tip card */}
              <div className="bg-[#2E7D32]/5 border border-[#2E7D32]/20 rounded-2xl p-4">
                <p className="text-sm font-bold text-[#2E7D32] mb-2">{language === 'nl' ? 'Tip' : 'Tip'}</p>
                <ul className="text-sm text-[#2A2A2A] space-y-1">
                  <li className="flex items-start gap-2">
                    <Check className="w-4 h-4 text-[#2E7D32] mt-0.5 flex-shrink-0" />
                    {language === 'nl' ? 'Vertel wie je bent (student, starter, etc.)' : 'Tell who you are (student, starter, etc.)'}
                  </li>
                  <li className="flex items-start gap-2">
                    <Check className="w-4 h-4 text-[#2E7D32] mt-0.5 flex-shrink-0" />
                    {language === 'nl' ? 'Noem je inkomenssituatie' : 'Mention your income situation'}
                  </li>
                  <li className="flex items-start gap-2">
                    <Check className="w-4 h-4 text-[#2E7D32] mt-0.5 flex-shrink-0" />
                    {language === 'nl' ? 'Houd het kort en persoonlijk' : 'Keep it short and personal'}
                  </li>
                </ul>
              </div>
            </div>
          )}

        </div>
      </div>

      {/* Bottom Action */}
      <div className="absolute bottom-0 left-0 right-0 z-30 bg-[#F7F7F7] border-t border-[#E5E5E5] p-4">
        <div className="max-w-xl mx-auto">
          {currentStep < TOTAL_STEPS ? (
            <Button
              onClick={() => setCurrentStep(currentStep + 1)}
              disabled={!canProceed()}
              className="w-full h-14 bg-[#2E7D32] hover:bg-[#256B2B] text-white rounded-xl font-bold text-lg disabled:opacity-50"
            >
              {language === 'nl' ? 'Volgende' : 'Next'}
              <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
          ) : (
            <Button
              onClick={handleSubmit}
              disabled={!canProceed() || loading}
              className="w-full h-14 bg-[#2E7D32] hover:bg-[#256B2B] text-white rounded-xl font-bold text-lg disabled:opacity-50"
            >
              {loading ? (language === 'nl' ? 'Bezig...' : 'Loading...') : (language === 'nl' ? 'Start swipen →' : 'Start swiping →')}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};

export default SwipeMatchPage;
